import { useEffect, useCallback } from 'react';
import { useQuery, NetworkStatus } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { ALL_CHAMPIONS_LEAGUE_GAME_DATA, CHAMPIONS_LEAGUE_SETTINGS } from '../../../graphql/queries';
import { 
  setAllGameData, 
  setAllGameDataLoading, 
  setAllGameDataError,
  setSettingsData,
  setSettingsDataLoading,
  setSettingsDataError
} from '../../../store/slices/gameSlice';

/**
 * Custom hook til at preloade leaderboard data når Champions League spillet loades
 * Dette sikrer at dataen allerede er i Redux når brugeren trykker på leaderboard
 * Data opdateres automatisk hver 30 sekunder for at reflektere admin ændringer
 */
export const usePreloadLeaderboardData = () => {
  const dispatch = useDispatch();
  
  // Tjek om data allerede er loaded i Redux
  const allGameData = useSelector((state) => state.game.allGameData);
  const settingsData = useSelector((state) => state.game.settingsData);
  
  // Hent alle Champions League spil data via Apollo
  const { 
    data: gameData, 
    loading: gameDataLoading, 
    error: gameDataError,
    refetch: refetchGameData,
    networkStatus: gameNetworkStatus
  } = useQuery(ALL_CHAMPIONS_LEAGUE_GAME_DATA, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Hent settings data via Apollo
  const { 
    data: settingsDataFromQuery, 
    loading: settingsLoadingFromQuery, 
    error: settingsErrorFromQuery,
    refetch: refetchSettings,
    networkStatus: settingsNetworkStatus
  } = useQuery(CHAMPIONS_LEAGUE_SETTINGS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Synkroniser Apollo game data med Redux
  useEffect(() => {
    const hasGameData = Boolean(gameData?.allChampionsLeagueGameData) || allGameData.length > 0;
    const blockingLoad =
      gameDataLoading &&
      !hasGameData &&
      gameNetworkStatus !== NetworkStatus.refetch;
    dispatch(setAllGameDataLoading(blockingLoad));
    
    if (gameDataError) {
      dispatch(setAllGameDataError(gameDataError.message));
    } else if (gameData?.allChampionsLeagueGameData) {
      dispatch(setAllGameData(gameData.allChampionsLeagueGameData));
    }
  }, [gameData, gameDataLoading, gameDataError, gameNetworkStatus, allGameData.length, dispatch]);

  // Synkroniser Apollo settings data med Redux
  useEffect(() => {
    const hasSettings =
      Boolean(settingsDataFromQuery?.championsLeagueSettings) ||
      Object.keys(settingsData).length > 0;
    const blockingLoad =
      settingsLoadingFromQuery &&
      !hasSettings &&
      settingsNetworkStatus !== NetworkStatus.refetch;
    dispatch(setSettingsDataLoading(blockingLoad));
    
    if (settingsErrorFromQuery) {
      dispatch(setSettingsDataError(settingsErrorFromQuery.message));
    } else if (settingsDataFromQuery?.championsLeagueSettings) {
      dispatch(setSettingsData(settingsDataFromQuery.championsLeagueSettings));
    }
  }, [
    settingsDataFromQuery,
    settingsLoadingFromQuery,
    settingsErrorFromQuery,
    settingsNetworkStatus,
    dispatch
  ]);

  // Manuel refetch funktion til at genindlæse alle data
  const refetchAll = useCallback(async () => {
    try {
      await Promise.all([
        refetchGameData(),
        refetchSettings()
      ]);
    } catch (error) {
      console.error('Error refetching leaderboard data:', error);
    }
  }, [refetchGameData, refetchSettings]);

  return {
    isLoading: gameDataLoading || settingsLoadingFromQuery,
    hasError: gameDataError || settingsErrorFromQuery,
    isDataLoaded: (allGameData && allGameData.length > 0) || (settingsData && Object.keys(settingsData).length > 0),
    refetchAll, // Manuel refetch funktion
    refetchGameData, // Individuel refetch for game data
    refetchSettings // Individuel refetch for settings
  };
};

